import type { ListingImage, ListingItem } from './types';
import { buildArticlePath } from '@lib/sanity/articlePage';
import { buildSanityImageUrl } from '@lib/sanity/image';
import { buildTeamRevealPath } from '@lib/sanity/teamReveal';
import { TEAM_REVEALS_SECTION_LABEL } from '@lib/article-seo';
import type { SanityListingCard } from '@lib/sanity/listings';

// Cards render at ~390px in the three-column grid; twice that covers retina.
export const CARD_IMAGE_WIDTH = 780;
const AVATAR_WIDTH = 80;
const FALLBACK_IMAGE_DIMENSIONS = { width: 390, height: 220 };
const AUTHOR_PATH_PREFIX = '/author';

const WORDS_PER_MINUTE = 200;
// Average English word plus the space after it.
const CHARACTERS_PER_WORD = 6;

// WordPress only exposes the primary category's display name.
const WORDPRESS_TEAM_REVEAL_CATEGORY = 'team reveal';

const HTML_TAG_PATTERN = /<[^>]*>/g;
const NUMERIC_ENTITY_PATTERN = /&#(x?)([0-9a-f]+);/gi;
const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  hellip: '…',
  ndash: '–',
  mdash: '—'
};
const NAMED_ENTITY_PATTERN = /&([a-z]+);/gi;
// WordPress closes a trimmed excerpt with "[...]" (or its entity form).
const EXCERPT_ELLIPSIS_PATTERN = /\s*\[(?:\.\.\.|…)\]\s*$/;
// Editors paste this summary block, robot emoji and all, into excerpts; it
// is not a lede.
const AI_SUMMARY_PATTERN = /\s*(?:🤖\s*)?Hub AI quick summary[\s\S]*$/u;
const FIRST_SENTENCE_PATTERN = /^[\s\S]*?[.!?](?=["')\]]?(?:\s|$))/;
const WHITESPACE_PATTERN = /\s+/g;

export type WordPressListingNode = {
  id: string;
  date: string;
  title: string;
  slug: string;
  excerpt?: string | null;
  primaryCategory?: string | null;
  author?: {
    node?: {
      name: string;
      slug: string;
      avatar?: { url?: string | null } | null;
    } | null;
  } | null;
  featuredImage?: {
    node?: {
      sourceUrl: string;
      altText?: string | null;
      mediaDetails?: {
        width?: number | null;
        height?: number | null;
      } | null;
    } | null;
  } | null;
  seo?: { readingTime?: number | string | null } | null;
};

export type WordPressPostsData = {
  posts?: {
    edges?: { node: WordPressListingNode }[];
    pageInfo?: { hasNextPage: boolean; endCursor: string | null };
  };
};

export const buildAuthorPath = (authorSlug: string): string =>
  `${AUTHOR_PATH_PREFIX}/${authorSlug}`;

const scaleToCardWidth = (dimensions?: {
  width: number;
  height: number;
}): Pick<ListingImage, 'width' | 'height'> => {
  if (!dimensions || dimensions.width <= 0 || dimensions.height <= 0) {
    return FALLBACK_IMAGE_DIMENSIONS;
  }
  const width = Math.min(CARD_IMAGE_WIDTH, dimensions.width);
  return {
    width,
    height: Math.round((width * dimensions.height) / dimensions.width)
  };
};

const toSanityCardImage = (
  featuredImage: SanityListingCard['featuredImage']
): ListingImage | undefined => {
  if (!featuredImage?.asset) return undefined;
  return {
    url: buildSanityImageUrl(featuredImage)
      .width(CARD_IMAGE_WIDTH)
      .fit('max')
      .url(),
    alt: featuredImage.alt ?? '',
    ...scaleToCardWidth(featuredImage.asset.metadata?.dimensions)
  };
};

export const toReadMinutesFromCharacters = (
  characterCount?: number | null
): number | undefined => {
  if (!characterCount || characterCount <= 0) return undefined;
  return Math.max(
    1,
    Math.round(characterCount / CHARACTERS_PER_WORD / WORDS_PER_MINUTE)
  );
};

const isWordPressTeamReveal = (primaryCategory?: string | null): boolean =>
  primaryCategory?.trim().toLowerCase() === WORDPRESS_TEAM_REVEAL_CATEGORY;

const decodeEntities = (text: string): string =>
  text
    .replace(NUMERIC_ENTITY_PATTERN, (_match, isHex: string, code: string) =>
      String.fromCodePoint(parseInt(code, isHex ? 16 : 10))
    )
    .replace(
      NAMED_ENTITY_PATTERN,
      (match, name: string) => NAMED_ENTITIES[name.toLowerCase()] ?? match
    );

/**
 * The opening sentence of a WordPress excerpt, as plain text. The excerpt is
 * a machine trim of the first paragraph, so it carries markup, a trailing
 * "[...]" and, on many posts, the pasted AI summary block.
 */
export const toStandfirstFromExcerpt = (
  excerpt?: string | null
): string | undefined => {
  if (!excerpt) return undefined;
  const plain = decodeEntities(excerpt.replace(HTML_TAG_PATTERN, ' '))
    .replace(WHITESPACE_PATTERN, ' ')
    .trim();
  const withoutEllipsis = plain
    .replace(EXCERPT_ELLIPSIS_PATTERN, '')
    .replace(AI_SUMMARY_PATTERN, '')
    .trim();
  if (!withoutEllipsis) return undefined;
  const firstSentence = FIRST_SENTENCE_PATTERN.exec(withoutEllipsis)?.[0];
  return (firstSentence ?? withoutEllipsis).trim();
};

export const normaliseSanityListingItem = (
  card: SanityListingCard
): ListingItem => {
  const isTeamReveal = card._type === 'teamReveal';
  const href =
    isTeamReveal && card.author
      ? buildTeamRevealPath(card.author.slug, card.slug)
      : buildArticlePath(card.slug);
  return {
    id: card._id,
    source: 'sanity',
    isTeamReveal,
    href,
    title: card.title,
    publishedAt: card.publishedAt ?? card._createdAt,
    categoryName:
      card.category?.title ??
      (isTeamReveal ? TEAM_REVEALS_SECTION_LABEL : undefined),
    categorySlug: card.category?.slug,
    standfirst: card.standfirst ?? undefined,
    image: toSanityCardImage(card.featuredImage),
    author: card.author
      ? {
          name: card.author.name,
          href: buildAuthorPath(card.author.slug),
          avatarUrl: card.author.avatar?.asset
            ? buildSanityImageUrl(card.author.avatar)
                .width(AVATAR_WIDTH)
                .height(AVATAR_WIDTH)
                .url()
            : undefined
        }
      : undefined,
    readMinutes: toReadMinutesFromCharacters(card.bodyCharacterCount),
    isPremium: card.accessLevel === 'premium',
    legacyHref: card.legacySlug ? `/${card.legacySlug}` : undefined
  };
};

const toReadMinutes = (
  readingTime: number | string | null | undefined
): number | undefined => {
  if (readingTime === null || readingTime === undefined) return undefined;
  const minutes = Number(readingTime);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : undefined;
};

export const normaliseWordPressListingItem = (
  node: WordPressListingNode
): ListingItem => {
  const image = node.featuredImage?.node;
  const author = node.author?.node;
  return {
    id: node.id,
    source: 'wordpress',
    isTeamReveal: isWordPressTeamReveal(node.primaryCategory),
    href: `/${node.slug}`,
    title: node.title,
    publishedAt: node.date,
    categoryName: node.primaryCategory ?? undefined,
    standfirst: toStandfirstFromExcerpt(node.excerpt),
    image: image
      ? {
          url: image.sourceUrl,
          alt: image.altText ?? '',
          width: image.mediaDetails?.width ?? FALLBACK_IMAGE_DIMENSIONS.width,
          height: image.mediaDetails?.height ?? FALLBACK_IMAGE_DIMENSIONS.height
        }
      : undefined,
    author: author
      ? {
          name: author.name,
          href: buildAuthorPath(author.slug),
          avatarUrl: author.avatar?.url ?? undefined
        }
      : undefined,
    readMinutes: toReadMinutes(node.seo?.readingTime),
    isPremium: false
  };
};
